import { shouldRetrySocket } from '@polygraf/shared'
import { useEffect, useRef } from 'react'

/**
 * While `intervalMs` is set, restart the socket's reconnect cycle every
 * `intervalMs` — but only when the page is visible and the socket is still
 * down (`shouldRetrySocket`). `PolygrafConnection` stops retrying after 5
 * attempts; this is what lets the live HTTP fallback end once the network lets
 * the socket through again (PR 713 review). `null` turns it off.
 */
export function useSocketRetry(
	intervalMs: number | null,
	connected: boolean,
	reconnect: () => void,
): void {
	// Refs, not dependencies: a connect or a new `reconnect` identity must not
	// restart the timer and push the next retry out by another full interval.
	const connectedRef = useRef(connected)
	connectedRef.current = connected
	const reconnectRef = useRef(reconnect)
	reconnectRef.current = reconnect

	useEffect(() => {
		if (intervalMs === null) return
		const timer = setInterval(() => {
			const doc = (globalThis as { document?: Document }).document
			if (
				shouldRetrySocket({
					pageHidden: doc?.hidden ?? false,
					socketConnected: connectedRef.current,
				})
			) {
				reconnectRef.current()
			}
		}, intervalMs)
		return () => clearInterval(timer)
	}, [intervalMs])
}
